// src/components/ProductDetail/ProductDetail.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ColorPicker from '../ColorPicker/Colorpicker';
import GeminiRecommendations from '../GeminiRecommendations/GeminiRecommendations';
import productItems from '../../data/ProductItems';
import './ProductDetail.css';

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const product = productItems.find(p => p.id === parseInt(productId));
  const modelViewerRef = useRef(null);

  const [selectedColor, setSelectedColor] = useState(product?.color || '');
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showARInstructions, setShowARInstructions] = useState(false);

  useEffect(() => {
    if (product) {
      setSelectedColor(product.color);
      checkIfWishlisted();
    }
  }, [product]);

  const checkIfWishlisted = () => {
    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    const isInWishlist = wishlist.some(item =>
      item.id === product.id && item.selectedColor === selectedColor
    );
    setIsWishlisted(isInWishlist);
  };

  if (!product) {
    return (
      <div className="product-not-found">
        <h2>Product not found</h2>
        <Link to="/" className="back-home-btn">Back to Home</Link>
      </div>
    );
  }

  const handleColorChange = (color) => {
    console.log('Color changing to:', color); // Debug
    setSelectedColor(color);

    // Update model-viewer variant
    const modelViewer = modelViewerRef.current;
    if (modelViewer) {
      try {
        // Try to change variant if available
        if (color !== 'Default' && color !== product.color) {
          modelViewer.variantName = color;
        } else {
          modelViewer.variantName = null;
        }
      } catch (error) {
        console.log('Variant change not supported for this model');
      }
    }

    // Recheck if this color variant is wishlisted
    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    const isInWishlist = wishlist.some(item =>
      item.id === product.id && item.selectedColor === color
    );
    setIsWishlisted(isInWishlist);
  };

  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToWishlist = () => {
    const wishlistItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      category: product.category,
      modelSrc: product.modelSrc,
      iOSSrc: product.iOSSrc,
      selectedColor: selectedColor,
      quantity: quantity,
      rating: product.rating,
      description: product.description,
      annotations: product.annotations || [],
      addedAt: new Date().toISOString()
    };

    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');

    const existingIndex = wishlist.findIndex(
      item => item.id === product.id && item.selectedColor === selectedColor
    );

    if (existingIndex > -1) {
      wishlist[existingIndex].quantity += quantity;
      alert(`✓ Updated ${product.name} (${selectedColor}) quantity in wishlist!`);
    } else {
      wishlist.push(wishlistItem);
      alert(`✓ ${product.name} (${selectedColor}) x${quantity} added to wishlist!`);
    }

    localStorage.setItem('wishlist', JSON.stringify(wishlist));
    setIsWishlisted(true);

    const goToWishlist = window.confirm('Item added to wishlist! Do you want to view your wishlist?');
    if (goToWishlist) {
      navigate('/wishlist');
    }
  };

  const handleRemoveFromWishlist = () => {
    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    const updatedWishlist = wishlist.filter(
      item => !(item.id === product.id && item.selectedColor === selectedColor)
    );

    localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
    setIsWishlisted(false);
    alert('✓ Removed from wishlist!');
  };

  const totalPrice = (parseFloat(product.price) * quantity).toFixed(2);

  return (
    <div className="product-detail-container">
      <div className="product-detail-wrapper">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <span>{product.category}</span>
          <span>/</span>
          <span>{product.name}</span>
        </div>

        <div className="product-detail-grid">
          {/* Left Side - 3D Model */}
          <div className="product-model-section">
            <model-viewer
              ref={modelViewerRef}
              src={product.modelSrc}
              ios-src={product.iOSSrc}
              alt={product.name}
              ar
              ar-modes="webxr scene-viewer quick-look"
              camera-controls
              auto-rotate
              shadow-intensity="1"
              environment-image="neutral"
              style={{ width: '100%', height: '600px' }}
            >
              {product.annotations?.map((annotation) => (
                <button
                  key={annotation.slot}
                  slot={annotation.slot}
                  data-position={annotation.position}
                  data-normal={annotation.normal}
                  data-orbit={annotation.orbit}
                  data-target={annotation.target}
                  className="hotspot"
                >
                  <div className="annotation-tooltip">{annotation.title}</div>
                </button>
              ))}

              <button slot="ar-button" className="ar-button">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                View in AR
              </button>
            </model-viewer>

            <div className="model-controls">
              <button
                className="info-btn"
                onClick={() => setShowARInstructions(!showARInstructions)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                AR Instructions
              </button>
            </div>

            {showARInstructions && (
              <div className="ar-instructions">
                <h4>📱 How to View in AR</h4>
                <ul>
                  <li>Click the "View in AR" button above</li>
                  <li>On mobile: Point your camera at a flat surface</li>
                  <li>Tap to place the product in your space</li>
                  <li>Walk around to view from all angles</li>
                  <li>Take photos to share with friends!</li>
                </ul>
              </div>
            )}

            {/* ✅ MOVED: Gemini Recommendations Below 3D Model */}
            <GeminiRecommendations
              currentProduct={product}
              allProducts={productItems}
            />
          </div>

          {/* Right Side - Product Info */}
          <div className="product-info-section">
            <div className="product-header">
              <div className="product-badges-row">
                {product.verified && (
                  <span className="verified-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M9 12L11 14L15 10M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12Z"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Verified
                  </span>
                )}
                {product.inStock && (
                  <span className="stock-badge">
                    ✓ In Stock
                  </span>
                )}
              </div>

              <h1>{product.name}</h1>

              <div className="rating-section">
                <div className="stars">
                  {'⭐'.repeat(Math.floor(parseFloat(product.rating)))}
                </div>
                <span className="rating-value">{product.rating}</span>
                <span className="rating-count">(128 reviews)</span>
              </div>

              <div className="price-section">
                <span className="current-price">${product.price}</span>
                <span className="original-price">${(parseFloat(product.price) * 1.3).toFixed(2)}</span>
                <span className="discount-badge">Save 30%</span>
              </div>
            </div>

            <div className="product-description">
              <h3>Description</h3>
              <p>{product.description}</p>
            </div>

            {/* Color Picker */}
            {product.colorOptions && product.colorOptions.length > 0 && (
              <div className="color-picker-wrapper">
                <ColorPicker
                  colorOptions={product.colorOptions}
                  selectedColor={selectedColor}
                  onColorChange={handleColorChange}
                  productName={product.name}
                />
              </div>
            )}

            {/* Quantity Selector */}
            <div className="quantity-section">
              <span className="quantity-label">Quantity:</span>
              <div className="quantity-controls">
                <button
                  className="quantity-btn"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                >
                  −
                </button>
                <span className="quantity-value">{quantity}</span>
                <button
                  className="quantity-btn"
                  onClick={() => handleQuantityChange(1)}
                  disabled={quantity >= 10}
                >
                  +
                </button>
              </div>
              <span className="quantity-total">Total: ${totalPrice}</span>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              {!isWishlisted ? (
                <button className="add-to-cart-btn" onClick={handleAddToWishlist}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.06211 22.0329 6.39464C21.7564 5.72718 21.351 5.12082 20.84 4.61Z"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Add to Wishlist
                </button>
              ) : (
                <button className="add-to-cart-btn" onClick={handleRemoveFromWishlist} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.06211 22.0329 6.39464C21.7564 5.72718 21.351 5.12082 20.84 4.61Z" />
                  </svg>
                  Remove from Wishlist
                </button>
              )}

              <Link to="/wishlist" className="view-wishlist-btn" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '16px 32px',
                background: 'white',
                border: '2px solid #e5e7eb',
                color: '#374151',
                textDecoration: 'none',
                borderRadius: '12px',
                fontWeight: '700',
                transition: 'all 0.3s ease'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" />
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                </svg>
                View Wishlist
              </Link>
            </div>

            {/* Product Details */}
            <div className="product-details">
              <h3>Product Details</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Category:</span>
                  <span className="detail-value">{product.category}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Color:</span>
                  <span className="detail-value">{selectedColor}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value">
                    {product.inStock ? '✓ In Stock' : '✗ Out of Stock'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">AR Ready:</span>
                  <span className="detail-value">✓ Yes</span>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="product-features">
              <h3>Key Features</h3>
              <ul>
                <li>✓ View in Augmented Reality</li>
                <li>✓ Blockchain Verified Authenticity</li>
                <li>✓ Free Shipping on Orders Over $100</li>
                <li>✓ 30-Day Return Policy</li>
                <li>✓ 1-Year Warranty</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;